import { useState } from "react";
import api, { errMsg } from "../lib/api";
import { useToast } from "../lib/toast";
import { Modal, Button, Field } from "./ui";
import { LinkIcon, CheckIcon, InfoIcon, ShieldIcon } from "./ui/Icons";

export default function NaqoodInfoModal({ file, onClose, onSuccess }) {
  const [organizationSlug, setOrganizationSlug] = useState(
    file?.naqood?.organizationSlug || ""
  );
  const [secretKey, setSecretKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const alreadyConnected = file?.naqood?.isConnected;

  const handleSubmit = async () => {
    if (!organizationSlug.trim() || !secretKey.trim()) {
      toast.error(
        "Both fields required",
        "Enter the organisation slug and secret key from Naqood."
      );
      return;
    }
    try {
      setLoading(true);
      const res = await api.put(`/api/files/${file._id}/naqood`, {
        organizationSlug: organizationSlug.trim(),
        secretKey: secretKey.trim(),
      });
      onSuccess(res.data.data);
      toast.success("Connected to Naqood", `Organisation: ${organizationSlug.trim()}`);
      onClose();
    } catch (err) {
      toast.error("Connection failed", errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      icon={LinkIcon}
      title="Naqood connection"
      description="Paste the organisation slug and secret key returned after authorising."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            {alreadyConnected ? "Update connection" : "Connect"}
          </Button>
        </>
      }
    >
      {alreadyConnected && (
        <div className="mb-5 rounded-xl border border-emerald-200 bg-gradient-to-b from-emerald-50 to-emerald-50/40 px-4 py-3.5">
          <div className="flex items-center gap-2.5">
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-emerald-100 text-emerald-700">
              <CheckIcon size={13} strokeWidth={3} />
            </span>
            <p className="text-[13px] font-medium text-emerald-900">
              Currently connected
            </p>
          </div>
          <div className="mt-2.5 rounded-lg border border-emerald-200/70 bg-white/70 px-3 py-2">
            <p className="text-[11px] uppercase tracking-wide text-emerald-700/70">
              Organisation
            </p>
            <p className="truncate font-mono text-[12.5px] text-emerald-800">
              {file.naqood.organizationSlug}
            </p>
          </div>
          <p className="mt-2.5 text-[12px] leading-relaxed text-emerald-800/80">
            Entering new values below replaces this connection. Everything already
            pushed stays where it is.
          </p>
        </div>
      )}

      {/* Where these two values come from — the step people forget. */}
      <div className="mb-5 rounded-xl border border-ink-200/70 bg-ink-50/60 px-4 py-3.5">
        <div className="flex items-center gap-2">
          <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-ink-200/70 text-ink-600">
            <InfoIcon size={12} />
          </span>
          <p className="text-[12.5px] font-medium text-ink-800">
            Where to find these
          </p>
        </div>
        <ol className="mt-2.5 space-y-1.5 text-[12.5px] leading-relaxed text-ink-600">
          <li className="flex gap-2">
            <span className="font-mono text-ink-400">1.</span>
            <span>Authorise this company in Naqood.</span>
          </li>
          <li className="flex gap-2">
            <span className="font-mono text-ink-400">2.</span>
            <span>
              The callback page shows{" "}
              <code className="rounded bg-ink-200/60 px-1 py-0.5 font-mono text-[11.5px] text-ink-700">
                organizationSlug
              </code>{" "}
              and{" "}
              <code className="rounded bg-ink-200/60 px-1 py-0.5 font-mono text-[11.5px] text-ink-700">
                secretKey
              </code>
              .
            </span>
          </li>
          <li className="flex gap-2">
            <span className="font-mono text-ink-400">3.</span>
            <span>Copy each one into the matching box below.</span>
          </li>
        </ol>
      </div>

      <div className="space-y-4">
        <Field
          label="Organisation slug"
          required
          hint="Lowercase, with hyphens — e.g. vm-media-llc. Must match Naqood exactly."
        >
          <input
            className="field-input font-mono text-[13px]"
            placeholder="your-organisation-slug"
            autoComplete="off"
            spellCheck={false}
            value={organizationSlug}
            onChange={(e) => setOrganizationSlug(e.target.value)}
          />
        </Field>

        <Field
          label="Secret key"
          required
          hint="Stored against this file only, and never shown again after saving."
        >
          <div className="relative">
            <input
              type={showKey ? "text" : "password"}
              className="field-input pr-16 font-mono text-[13px]"
              placeholder="pk_live_…"
              autoComplete="off"
              spellCheck={false}
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
            />
            {secretKey && (
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-[11.5px] font-medium text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-700"
              >
                {showKey ? "Hide" : "Show"}
              </button>
            )}
          </div>
        </Field>
      </div>

      <div className="mt-4 flex items-start gap-2 text-[12px] leading-relaxed text-ink-500">
        <span className="mt-0.5 shrink-0 text-ink-400">
          <ShieldIcon size={13} />
        </span>
        <p>
          The key gives full access to this organisation's books — keep it out of
          shared sheets and chats.
        </p>
      </div>
    </Modal>
  );
}
